import createCrudService from '@core/api/create-crud-service';
import { queryClient } from '@core/api/query-client';
import { QUERY_KEYS } from '@src/shared/constants/query-keys';
import { request } from '@core/api/request';

import type { TLead } from '@shared/types/lead';
import { useMutation } from '@tanstack/react-query';
import { showToast } from '@src/shared/utils/helpers/show-toast';

export type TLeadQuery = {
  page_number?: number;
  page_size?: number;
  referal?: (number | string)[];
  leadSource?: string;
  q?: string;
  rank?: string;
  source?: string;
  calendly_event_type?: string;
  term?: string;
  excluded_lead_ids?: number;
  is_manual?: boolean;
  import_history?: (number | string)[];
};

export type TEditLeadBody = {
  id: number;
  phone_number: string;
  labels: number[];
  programs: number[];

  name: string;
  email: string;
  whatsapp_number: string;
  birth_date: string;

  gender: TLead['gender'];
  source: TLead['source'];
  english_level: TLead['english_level'];
  rank: TLead['rank'];

  source_note: string;
  metadata: TLead['metadata'];
  main_info: string;
  next_follow_up_date: string;

  status: number;
  referal: number;
  student: number;
  db_student: number;
  assignee: number | null;
  partner?: number | null;
  created_by: number;
  creator_member: number;
  calendly_event_type: number;
  custom_fields: { label: string; value: string[] }[];
  university: number | null;
};

// LEAD BULK ASSIGN LABELS
type TLeadBulkAssignLabelsBody = {
  ids: number[];
  labels: number[];
};

type TLeadBulkUpdateStatusBody = {
  ids: number[];
  status: number;
};

// LEAD API
const leadApi = {
  ...createCrudService<TLead, TLeadQuery, TEditLeadBody>({
    queryKey: [QUERY_KEYS.getLeads],
    url: 'leads',
  }),

  useBulkAssignLabels: () =>
    useMutation({
      mutationFn: async (body: TLeadBulkAssignLabelsBody) =>
        await request<void>({
          url: 'leads/bulk-assign-labels/',
          method: 'PATCH',
          body,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getLeads] });
        showToast({
          title: 'Bulk Assign Labels Success',
          description: 'Labels have been assigned to the leads successfully',
          variant: 'success',
        });
      },
    }),

  useBulkUpdateStatus: () =>
    useMutation({
      mutationFn: async (body: TLeadBulkUpdateStatusBody) =>
        await request<void>({
          url: 'leads/bulk-update-status/',
          method: 'POST',
          body,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getLeads] });
        showToast({
          title: 'Status updated',
          description: 'Lead statuses have been updated successfully',
          variant: 'success',
        });
      },
    }),
};

// LEAD BULK ASSIGN TO USER
export type TLeadBulkAssignBody = {
  ids: number[];
  assignee: number | null;
};
const leadBulkAssign = createCrudService<
  { detail: string },
  {},
  TLeadBulkAssignBody
>({
  queryKey: [QUERY_KEYS.getLeads, 'bulk-assign'],
  url: 'leads/bulk-assign',
});

const importLeads = createCrudService<
  { detail: string },
  {},
  { file: File; programs: number[] }
>({
  queryKey: [QUERY_KEYS.getLeads, 'import'],
  url: 'leads/import-leads',
});

export default leadApi;

export { leadBulkAssign, importLeads };
